import {useQuery, NetworkStatus} from "@apollo/client";
import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import LoadingButton from "@unibuddy/machop/Experimental/General/components/LoadingButton/LoadingButton";
import useAnalytics from "@unibuddy/machop/AnalyticsNew/hooks/useAnalytics";
import {
    useBuddiesFilters,
    useSyncUrlOnce,
} from "@unibuddy/machop/Buddies/components/BuddiesFiltersProvider/BuddiesFiltersProvider";
import {
    Body,
    Container,
    Header,
} from "@unibuddy/machop/Register/components/VerifyLayout/Layout";
import PoweredByLogo from "@unibuddy/machop/Experimental/General/components/PoweredByLogo/PoweredByLogo";
import {useWindowTitle} from "../../../utils";
import buddiesQuery from "../../queries/buddiesQuery";
import BuddyListCard from "../BuddyListCard/BuddyListCard";
import BuddiesFilterSection from "../BuddiesFilterSection/BuddiesFilterSection";
import s from "./Buddies.pcss";
import useScrollWindowToTop from "../../../shared/functions/useScrollWindowToTop";

const BuddyCardContainer = (props) => {
    return (
        <li {...props} className={s.cardContainer}>
            {props.children}
        </li>
    );
};

const TopBar = (props) => {
    return <div className={s.topBar}>{props.children}</div>;
};

/* eslint-disable */
const poweredByUnibuddy = (
    <a
        href="https://unibuddy.com"
        aria-label="Powered by Unibuddy"
        target="_blank"
        className={s.poweredByUnibuddy}
    >
        <PoweredByLogo />
    </a>
);
/* eslint-enable */

const BuddiesList = ({limit, filters, buddiesListViewedMore}) => {
    const [skip, setSkip] = useState(0);
    const variables = {
        skip: 0,
        limit,
        ...filters,
        degreeCategoryId: filters.subCategory || filters.subject || undefined,
    };
    const {loading, error, data, fetchMore, networkStatus} = useQuery(buddiesQuery, {
        variables,
        notifyOnNetworkStatusChange: true,
    });

    if (loading && networkStatus !== NetworkStatus.fetchMore) {
        return (
            <div
                className={s.emptyContainer}
                data-test-id="buddies-list-loading"
            >
                Loading...
            </div>
        );
    }
    if (error) {
        console.log(error);
        return "Error";
    }

    if (!data.marketplaceMentorList.mentors.length) {
        return (
            <div className={s.emptyContainer}>
                <div className={s.emptyContent}>
                    Sorry, no ambassador is matching your
                    filters.
                </div>
            </div>
        );
    }

    const hasMore = !(
        data.marketplaceMentorList.mentors.length <
        skip + limit
    );
    const seeMore = () => {
        setSkip(data.marketplaceMentorList.mentors.length);
        buddiesListViewedMore({
            pageNumber: Math.ceil(
                data.marketplaceMentorList.mentors.length / limit,
            ),
        });
        fetchMore({
            variables: {
                ...filters,
                mentorSeed: data.marketplaceMentorList.mentorSeed,
                universitySeed: data.marketplaceMentorList.universitySeed,
                skip: data.marketplaceMentorList.mentors.length,
            },
        });
    };

    return (
        <React.Fragment>
            <ul
                className={s.list}
                data-test-id="buddies-list"
            >
                {data.marketplaceMentorList.mentors.map(
                    (mentor, index) => {
                        return (
                            <BuddyCardContainer
                                data-test-id="buddy-card-container"
                                key={mentor.id}
                            >
                                <BuddyListCard
                                    mentor={mentor}
                                    position={index + 1}
                                />
                            </BuddyCardContainer>
                        );
                    },
                )}
            </ul>
            <div className={s.loadMoreButton}>
                <LoadingButton
                    block
                    size="lg"
                    data-test-id="load-more-buddies"
                    loading={networkStatus === NetworkStatus.fetchMore}
                    className={classNames({
                        [s.invisible]: !hasMore,
                    })}
                    aria-hidden={!hasMore}
                    onClick={seeMore}
                >
                    See more
                </LoadingButton>
            </div>
        </React.Fragment>
    );
};

export default function Buddies({limit}) {
    const title = "Chat to uni students";
    useWindowTitle(title);
    useScrollWindowToTop();
    const {filters, setFilters} = useBuddiesFilters();
    useSyncUrlOnce(filters);

    const {
        events: {buddiesListViewed, buddiesListViewedMore},
    } = useAnalytics();
    useEffect(
        () => {
            buddiesListViewed();
        },
        [buddiesListViewed],
    );

    return (
        <Container id="buddies-page" dataTestId={"buddies-page"}>
            <Header>
                <div>
                    <h1 data-test-id="title" className={s.title}>
                        {title}
                    </h1>
                    <p className={s.brag}>
                        Choose a uni, degree subject or select by their home
                        country, and find out what life’s really like at uni
                    </p>
                    <div className={s.poweredByUnibuddyWrapper}>
                        {poweredByUnibuddy}
                    </div>
                </div>
            </Header>

            <Body>
                <TopBar>
                    <BuddiesFilterSection
                        filters={filters}
                        setFilters={setFilters}
                    />
                </TopBar>

                <BuddiesList limit={limit} filters={filters} buddiesListViewedMore={buddiesListViewedMore} />
            </Body>
        </Container>
    );
}

Buddies.propTypes = {
    limit: PropTypes.number,
};

Buddies.defaultProps = {
    limit: 18,
};
